/**
 * Persistencia de TanStack Query en IndexedDB (caché in-page persistente, §11.2).
 *
 * La pelea actual, las métricas, mis apuestas y los catálogos sobreviven al cierre del
 * navegador. `maxAge` 24 h. El nombre de la base lleva `VITE_CACHE_VERSION`, así que subir
 * la versión invalida todo sin migraciones.
 */
import { QueryClient } from '@tanstack/react-query';
import type { PersistedClient, Persister } from '@tanstack/react-query-persist-client';
import { db } from './db.js';

const CACHE_KEY = 'query-cache';
export const CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // 10 s: navegar entre vistas no dispara refetch (la caché persistida pinta al
        // instante); las mutaciones invalidan por queryKey, así que la frescura se
        // mantiene donde importa sin recargar todo.
        staleTime: 10_000,
        gcTime: CACHE_MAX_AGE_MS,
        retry: (failureCount, error) => {
          const status = (error as unknown as { status?: number }).status;
          if (status !== undefined && status < 500) return false;
          return failureCount < 3;
        },
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        networkMode: 'offlineFirst',
      },
      mutations: {
        retry: 0,
        networkMode: 'offlineFirst',
      },
    },
  });
}

/** Persister de TanStack Query sobre Dexie. */
export const dexiePersister: Persister = {
  async persistClient(client: PersistedClient) {
    await db.cache.put({ key: CACHE_KEY, value: client, updatedAt: Date.now() });
  },
  async restoreClient() {
    const row = await db.cache.get(CACHE_KEY);
    if (!row) return undefined;
    if (Date.now() - row.updatedAt > CACHE_MAX_AGE_MS) {
      await db.cache.delete(CACHE_KEY);
      return undefined;
    }
    return row.value as PersistedClient;
  },
  async removeClient() {
    await db.cache.delete(CACHE_KEY);
  },
};

export async function clearPersistedCache(): Promise<void> {
  await db.cache.clear();
}
